import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, TextField, IconButton, Badge, Fab } from '@mui/material';
import { Chat as ChatIcon, Send as SendIcon, Remove as MinimizeIcon } from '@mui/icons-material';
import { socketService } from '../../services/socket.service';
import { useAuth } from '../../hooks/useAuth';

interface WhisperMessage {
  senderId: string;
  senderName: string;
  message: string;
  timestamp: string;
}

interface WhisperChatProps {
  sessionId: string;
}

const WhisperChat: React.FC<WhisperChatProps> = ({ sessionId }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<WhisperMessage[]>([]);
  const [input, setInput] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsub = socketService.onWhisperMessage((data) => {
      setMessages((prev) => [...prev, data]);
      if (collapsed) setUnread((prev) => prev + 1);
    });
    return unsub;
  }, [collapsed]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    socketService.sendWhisper(sessionId, input.trim());
    setInput('');
  };

  const handleExpand = () => {
    setCollapsed(false);
    setUnread(0);
  };

  if (collapsed) {
    return (
      <Box sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1200 }}>
        <Badge badgeContent={unread} color="error">
          <Fab size="small" color="primary" onClick={handleExpand} title="Instructor Chat">
            <ChatIcon />
          </Fab>
        </Badge>
      </Box>
    );
  }

  return (
    <Paper sx={{ position: 'fixed', bottom: 16, left: 16, width: 320, maxHeight: 400, zIndex: 1200, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 2, boxShadow: 6 }}>
      <Box sx={{ p: 1.5, bgcolor: 'primary.main', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2">Instructor Chat</Typography>
        <IconButton size="small" onClick={() => setCollapsed(true)} sx={{ color: '#fff' }}>
          <MinimizeIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, maxHeight: 280, minHeight: 120 }}>
        {messages.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 4 }}>
            Private channel for instructors only
          </Typography>
        )}
        {messages.map((msg, i) => (
          <Box key={i} sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight={600} color={msg.senderId === user?.id ? 'primary.main' : 'text.primary'}>
              {msg.senderId === user?.id ? 'You' : msg.senderName}
            </Typography>
            <Typography variant="body2">{msg.message}</Typography>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Message instructors..."
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 500))}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
        />
        <IconButton size="small" color="primary" onClick={handleSend} disabled={!input.trim()}>
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default WhisperChat;
